import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag } from '../../hooks/useTags';
import downArrow from '../../assets/images/down-arrow.svg';

export interface TagOption {
  id: number;
  name: string;
}

interface TagMultiSelectProps {
  label?: string;
  tags: Tag[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  label,
  tags,
  selectedTagIds,
  onChange,
  placeholder = 'Select tags',
  disabled = false,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || containerRef.current.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const selectedTags = useMemo(() => {
    if (!selectedTagIds.length) {
      return [];
    }

    const selectedSet = new Set(selectedTagIds);
    return tags.filter((tag) => selectedSet.has(tag.id));
  }, [selectedTagIds, tags]);

  const buttonLabel = useMemo(() => {
    if (isLoading) {
      return 'Loading tags...';
    }

    if (!selectedTags.length) {
      return placeholder;
    }

    if (selectedTags.length <= 2) {
      return selectedTags.map((tag) => tag.name).join(', ');
    }

    const [first, second] = selectedTags;
    const remainingCount = selectedTags.length - 2;
    return `${first.name}, ${second.name} +${remainingCount} more`;
  }, [isLoading, placeholder, selectedTags]);

  const handleToggle = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => a.name.localeCompare(b.name));
  }, [tags]);

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {label && <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className="focus:ring-blue-primary flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={disabled || isLoading}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">{buttonLabel}</span>
          <img src={downArrow} alt="" className={`ml-2 h-2 w-2 transform transition ${isOpen ? '' : 'rotate-180'}`} />
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : sortedTags.length ? (
                sortedTags.map((tag) => {
                  const isChecked = selectedTagIds.includes(tag.id);
                  return (
                    <label
                      key={tag.id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                        checked={isChecked}
                        onChange={() => handleToggle(tag.id)}
                      />
                      <div className="font-medium">{tag.name}</div>
                    </label>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">No tags available.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TagMultiSelect;
